// Platform standard: money and rates always render to exactly this many
// decimals. Counts stay whole, and so do compact chart axis ticks.
export const DISPLAY_DECIMALS = 2;

// Compact number abbreviation (K / M / B), trimming trailing ".0" ("1.0K" → "1K").
// Returns "—" for null / NaN / non-positive values (the "no data" placeholder).
export function formatCompact(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v) || v <= 0) return '—';
  const abs = Math.abs(v);
  if (abs >= 1_000_000_000) return trimZero((v / 1_000_000_000).toFixed(2)) + 'B';
  if (abs >= 1_000_000) return trimZero((v / 1_000_000).toFixed(2)) + 'M';
  if (abs >= 1_000) return trimZero((v / 1_000).toFixed(2)) + 'K';
  return v.toFixed(2).replace(/\.?0+$/, '');
}

// Creator-tier range display: "10000-50000" → "10K – 50K". Falls back to the raw
// string for anything that isn't a clean "min-max" number pair.
export function formatTierRange(value: string): string {
  const parts = value.split('-');
  if (parts.length !== 2) return value;
  const min = Number(parts[0]);
  const max = Number(parts[1]);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return value;
  return `${formatCompact(min)} – ${formatCompact(max)}`;
}

// Indian rupee formatting, switching to Lakh (L) / Crore (Cr) at 1e5 / 1e7 and
// Indian comma grouping below 1L. Always two decimals, abbreviated units
// included — money never rounds to a whole unit (platform standard).
export function formatCurrencyINR(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  const abs = Math.abs(v);
  // Sign leads the symbol, matching the Intl output formatMoneyExact returns below.
  const sign = v < 0 ? '-' : '';
  if (abs >= 1_00_00_000) return `${sign}₹${(abs / 1_00_00_000).toFixed(DISPLAY_DECIMALS)} Cr`;
  if (abs >= 1_00_000) return `${sign}₹${(abs / 1_00_000).toFixed(DISPLAY_DECIMALS)} L`;
  return formatMoneyExact(v);
}

const INR_EXACT = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: DISPLAY_DECIMALS,
  maximumFractionDigits: DISPLAY_DECIMALS,
});

// Exact rupee amount: Indian grouping, always 2 decimals, never abbreviated (contrast
// formatCurrencyINR).
export function formatMoneyExact(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return INR_EXACT.format(v);
}

const MONEY_SHRINK_STEPS: ReadonlyArray<{ minLength: number; className: string }> = [
  { minLength: 16, className: 'text-[11px]' },
  { minLength: 14, className: 'text-[12px]' },
];

// Font-size class shrinking long exact amounts to fit a fixed-width cell; '' keeps the
// inherited size.
export function moneyFitClass(formatted: string): string {
  return MONEY_SHRINK_STEPS.find((s) => formatted.length >= s.minLength)?.className ?? '';
}

const CPV_CURRENCY_SYMBOLS: Record<string, string> = { INR: '₹' };

// Cost-per-view display, always two decimals ("₹0.09"); 0 / missing → "—".
// An unmapped currency shows its ISO code so a non-INR value isn't mislabelled as
// rupees; no currency → INR.
export function formatCpv(v: number | null | undefined, currency?: string | null): string {
  if (v == null || !Number.isFinite(v) || v <= 0) return '—';
  const code = currency || 'INR';
  const symbol = CPV_CURRENCY_SYMBOLS[code] ?? `${code} `;
  return `${symbol}${v.toFixed(DISPLAY_DECIMALS)}`;
}

// Indian-grouped integer formatter for the rare callsite that needs raw counts
// without K/M/B abbreviation.
export function formatIntegerIN(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return new Intl.NumberFormat('en-IN').format(v);
}

function trimZero(s: string): string {
  // Remove trailing zeros after decimal: "1.50" → "1.5", "1.00" → "1", "1.20" → "1.2"
  return s.includes('.') ? s.replace(/\.?0+$/, '') : s;
}
